import type { CssVars } from '../../../css-vars';
import { pxVar } from '../../../mixins/number-with-unit/number-with-unit.css-vars';
import type { VideoEntity, VideoPlayButtonSize } from '../schemas';

type VideoWidgetData = VideoEntity['data'];

/** A `null` value removes the property, so the declaration falls back to its LESS default. */
export type VideoCssVars = CssVars;

/**
 * Concrete pixel box for each play-button size preset. Single source of truth
 * that turns the stored `'S' | 'M' | 'L'` preset into rendered dimensions:
 * Small 36×36, Middle 48×48, Large 60×60.
 */
export const VIDEO_PLAY_BUTTON_SIZE_PX: Record<VideoPlayButtonSize, number> = {
    S: 36,
    M: 48,
    L: 60,
};

/**
 * The play icon scales with the button box — the default L preset renders a
 * 24px icon inside a 60px button (0.4), so the S/M presets stay visually
 * proportional (≈14 / ≈19px) instead of a fixed icon rattling inside a small
 * circle.
 */
const PLAY_BUTTON_ICON_RATIO = 0.4;

/**
 * Build the play-button CSS variable bag from the widget data. Shared verbatim
 * by both placement variants (the `playButton` field lives on the common data
 * base). Returns an empty object when `playButton` is absent so every
 * declaration falls back to its LESS default.
 */
export function getVideoPlayButtonCssVars(data: VideoWidgetData): VideoCssVars {
    const playButton = data.playButton;
    if (!playButton) {
        return {};
    }

    const size = VIDEO_PLAY_BUTTON_SIZE_PX[playButton.size] ?? VIDEO_PLAY_BUTTON_SIZE_PX.L;

    return {
        '--play-btn-size': pxVar(size),
        '--play-btn-icon-size': pxVar(Math.round(size * PLAY_BUTTON_ICON_RATIO)),
        '--play-btn-background-color': playButton.backgroundColor || null,
        '--play-btn-background-color-hover': playButton.hoverBackgroundColor || null,
        '--play-btn-icon-color': playButton.iconColor || null,
        '--play-btn-icon-color-hover': playButton.hoverIconColor || null,
    };
}
